import localForage from "localforage/dist/localforage";
import ApiClient from "@/api/ApiClient.ts";
import { loginThroughAnyServer } from "@/api/tenant";
import { isAreaSanitariaDeVigo } from "@/utils/user-types";
import Vue from "vue";

const state = {
  operator: null,
  operatorsByCenter: null,
  token: null,
  id: null,
  roles: [],
  subscriberId: null,
  operatorsRols: [],
  secrets: {},
};

const mutations = {
  setOperator(state, operator) {
    state.operator = operator;
  },

  setOperatorsByCenter(state, operators) {
    state.operatorsByCenter = operators;
  },

  setToken(state, token) {
    state.token = token;
  },

  setUserId(state, id) {
    state.id = id;
  },

  setRoles(state, roles) {
    state.roles = roles;
  },

  setSubscriberId(state, subscriberId) {
    state.subscriberId = subscriberId;
  },

  setOperatorsRols(state, rols) {
    state.operatorRols = rols;
  },

  setSecrets(state, secrets) {
    Vue.set(state, "secrets", secrets);
  },
};

const actions = {
  async login({ commit, dispatch }, userInfo) {
    const { username, password } = userInfo;
    try {
      let response;
      try {
        response = await new ApiClient().get("/operators/login", {
          params: {
            opUser: username.trim(),
            pwd: password,
          },
        });
      } catch (e) {
        response = await loginThroughAnyServer(username.trim(), password);
        if (!response) {
          throw new Error("Could not login the user");
        }
      }

      if (response?.data?.operator?.deleted) {
        throw new Error("User is deleted");
      }

      dispatch("app/reset", null, { root: true });
      dispatch("cache/reset", null, { root: true });
      dispatch("persistedCache/reset", null, { root: true });
      dispatch("services/clear", null, { root: true });
      dispatch("home/reset", null, { root: true });

      const data = response.data;
      localStorage.setItem("opId", data.id);
      commit("setToken", data.id);
      commit("setUserId", data.id);
      commit("setOperator", data.operator);
      commit("setSecrets", data.secrets);

      if (data.operator.data?.access && data.operator.data.access.length > 0) {
        const wgs = [];
        data.operator.data.access.forEach((access) => {
          if (data.callcenter.wgs.find((wg) => wg.id === access.wgId)) {
            wgs.push(data.callcenter.wgs.find((wg) => wg.id === access.wgId));
          }
        });
        data.callcenter.wgs = wgs;
      }

      dispatch("app/setCallCenter", data.callcenter, { root: true });
      dispatch("clientUserRegisterData/createPhoneExt", null, { root: true });

      commit("operators/setOperatorsByCenter", data.callcenter.operators, { root: true });
      commit("clientConfigs/setStateColors", data.callcenter.data.servicesAndTripsRowsColors, {
        root: true,
      });

      const { urlBase } = data.callcenter.wgs[0];
      localStorage.setItem("tenantUrl", urlBase + ":7000/");
      localStorage.setItem("tenantPbxUrl", urlBase + ":8500/");

      if (data.operator) {
        let {
          rol: { name },
        } = data.operator;
        let roles = [];
        roles.push(name.toLowerCase());
        commit("setRoles", roles);

        if (data.operator.credentials) {
          const { subscriberId } = data.operator.credentials;
          commit("setSubscriberId", subscriberId);
        }
      }

      const lastUsers = localStorage.getItem("lastUsers")
        ? JSON.parse(localStorage.getItem("lastUsers"))
        : {};

      const displayName = data.operator ? data.operator.name : username;
      if (!lastUsers[username] || (lastUsers[username] !== displayName && data.operator)) {
        lastUsers[username] = displayName;
      }

      localStorage.setItem("lastUsers", JSON.stringify(lastUsers));

      return data;
    } catch (error) {
      console.error("ERROR en login: ", error.message);
      throw error;
    }
  },

  logout({ commit, dispatch }) {
    commit("setToken", "");
    commit("setSubscriberId", null);
    dispatch("app/reset", null, { root: true });
    dispatch("cache/reset", null, { root: true });
    dispatch("persistedCache/reset", null, { root: true });
    dispatch("services/clear", null, { root: true });
    dispatch("home/reset", null, { root: true });
    window.localStorage.removeItem("opId");
    localForage.clear();

    if (typeof window.triggerLogout === "function") {
      window.triggerLogout();
      return false;
    }

    return true;
  },

  async getOperatorById({ commit }, idOperator) {
    const params = {
      opId: idOperator,
    };
    try {
      let response = await ApiClient().get("/operators", {
        params,
      });
      commit("setOperator", response.data);
      return response.data;
    } catch (error) {
      console.error("ERROR en getOperatorById: ", error.message);
    }
  },

  async getOperatorsByCentralId({ commit }, idCenter) {
    const params = {
      ccenterId: idCenter,
    };
    try {
      let response = await ApiClient().get("/operators", {
        params,
      });
      commit("setOperatorsByCenter", response.data);
      return response.data;
    } catch (error) {
      console.error("ERROR en getOperatorsByCentralId: ", error.message);
    }
  },

  async getOperator(_, customParams) {
    const params = {
      ...customParams,
    };
    try {
      let response = await ApiClient().get("/operators", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("ERROR en getOperator: ", error.message);
      return false;
    }
  },

  async getOperators(_, customParams) {
    const params = {
      ...customParams,
    };
    try {
      let { data } = await ApiClient().get("/operators", { params });
      return data;
    } catch (error) {
      console.error("ERROR en getOperators: ", error.message);
      return false;
    }
  },

  async createOperator(_, payload) {
    try {
      await ApiClient().post("/operators", payload.data);
      return true;
    } catch (error) {
      console.error("ERROR en createOperator: ", error.response);
      if (error.response) {
        return error.response.data;
      } else {
        return false;
      }
    }
  },

  async updateOperator(_, payload) {
    const params = {
      ...payload.params,
    };
    try {
      await ApiClient().put("/operators", payload.data, {
        params,
      });
      return true;
    } catch (error) {
      console.error("ERROR en updateOperator: ", error.message);
      if (error.response.data) {
        return error.response.data;
      }
      return false;
    }
  },

  async deleteOperator(_, customParams) {
    const params = {
      ...customParams,
    };
    try {
      await ApiClient().delete("/operators", {
        params,
      });
      return true;
    } catch (error) {
      console.error("ERROR en deleteOperator: ", error.message);
      return false;
    }
  },

  async getOperatorsRoles({ commit }, customParams) {
    const params = {
      ...customParams,
    };
    try {
      let { data } = await ApiClient().get("/operators/rols", { params });
      commit("setOperatorsRols", data);
      return data;
    } catch (error) {
      console.error("ERROR en getOperatorsRoles: ", error.message);
      return false;
    }
  },

  async createOperatorsRol(_, payload) {
    const params = {
      ...payload.params,
    };
    try {
      await ApiClient().post("/operators/rols", payload.data, {
        params,
      });
      return true;
    } catch (error) {
      console.error("ERROR en createOperatorsRol: ", error.response);
      if (error.response.data) {
        return error.response.data;
      }
      return false;
    }
  },

  async updateOperatorsRol(_, payload) {
    const params = {
      ...payload.params,
    };
    try {
      await ApiClient().put("/operators/rols", payload.data, {
        params,
      });
      return true;
    } catch (error) {
      console.error("ERROR en updateOperatorsRol: ", error.message);
      if (error.response.data) {
        return error.response.data;
      }
      return false;
    }
  },

  async deleteOperatorsRol(_, params) {
    try {
      await ApiClient().delete("/operators/rols", {
        params,
      });
      return true;
    } catch (error) {
      console.error("ERROR en deleteOperatorsRol: ", error.message);
      return false;
    }
  },
};

const getters = {
  operator(state) {
    return state.operator;
  },

  operatorIdCallcenter(state) {
    return state.operator?.idCallcenter;
  },

  operatorsByCenter(state) {
    return state.operatorsByCenter.filter((o) => !o.deleted);
  },

  roles(state) {
    return state.operatorRols;
  },

  token(state) {
    return state.token;
  },

  userId(state) {
    return state.id;
  },

  subscriberId(state) {
    return state.subscriberId;
  },

  operatorsRolsToSelect(state) {
    return (
      state.operatorRols?.map((rol) => {
        return {
          label: rol.name,
          value: rol.id,
        };
      }) || []
    );
  },

  hasAccessTo(state) {
    return (route) => {
      return state.operator?.rol?.data?.allowedMenuRoutes?.includes(route);
    };
  },

  currentRole(state) {
    return state.operator?.rol;
  },

  userHasManagementPermission(state) {
    if (isAreaSanitariaDeVigo()) {
      return false;
    }
    return state.operator?.rol?.data?.topBarButtons || false;
  },

  secret(state) {
    return async (key) => {
      return state.secrets?.[key];
    };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
